import { useConnection } from "@solana/wallet-adapter-react";
import { fromLamports, NATIVE_SOL } from "@stepfinance/common";
import React, { useContext, useEffect, useState } from "react";

import type { StakeAccountMeta } from "../actions/staking";
import { findStakeAccountMetas } from "../actions/staking";
import { DUST_THRESHOLD, useHideDustConfig } from "./app/config";
import { useEnrichedWallet } from "./enrichedWallet";
import { useMarkets } from "./market";
import { useRefreshContext } from "./refresh";

interface ValidatorStakingContextState {
  setValidatorStakingBalance: (balance: number) => void;
  validatorStakingBalance: number;
  stakeAccounts: StakeAccountMeta[];
}

const ValidatorStakingContext = React.createContext<null | ValidatorStakingContextState>(null);

export function ValidatorStakingContextProvider({ children }) {
  const [validatorStakingBalance, setValidatorStakingBalance] = useState(0);
  const [stakeAccounts, setStakeAccounts] = useState<StakeAccountMeta[]>([]);

  const { connection } = useConnection();
  const { refreshing } = useRefreshContext();
  const { publicKey } = useEnrichedWallet();
  const { priceInUSD } = useMarkets();
  const { hideDust } = useHideDustConfig();

  useEffect(() => {
    if (!publicKey || !connection) {
      setStakeAccounts([]);
      return;
    }

    let subscribed = true;
    if (refreshing) {
      subscribed = true;
    }
    const getStakeAccounts = async () => {
      if (subscribed) {
        const res = await findStakeAccountMetas(connection, publicKey);
        if (res) {
          if (hideDust) {
            const solPrice = priceInUSD(NATIVE_SOL.toBase58()) ?? 0;
            const filteredPositions = res.filter((acct) => {
              const value = fromLamports(acct.lamports, 9) * solPrice;
              return (value || 0) > DUST_THRESHOLD;
            });
            setStakeAccounts(filteredPositions);
          } else {
            setStakeAccounts(res);
          }
        }
      }
    };
    void getStakeAccounts();
    return () => {
      subscribed = false;
    };
  }, [connection, hideDust, priceInUSD, publicKey, refreshing]);

  return (
    <ValidatorStakingContext.Provider
      value={{
        setValidatorStakingBalance,
        validatorStakingBalance,
        stakeAccounts,
      }}
    >
      {children}
    </ValidatorStakingContext.Provider>
  );
}

export function useValidatorStakingContext(): ValidatorStakingContextState {
  const ctx = useContext(ValidatorStakingContext);
  if (ctx === null) {
    throw new Error("Context not available");
  }
  return ctx;
}
